import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { OptimizedImage } from '../utils/performance';

const HeroSection = () => {
  const mintingUrl = "https://og.rarible.com/token/0xb66a603f4cfe17e3d27b87a8bfcad319856518b8:66443457087237224376496623462586478899471087482130632045442236354399968428034";

  const scrollToMinting = () => {
    const element = document.getElementById('minting');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-[120vh] flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <OptimizedImage 
          src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=1920&q=80"
          alt="Modern Crypto Investment Background"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-purple-900/50"></div>
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-10">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-purple-400 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-20 text-center px-4 sm:px-6 max-w-7xl mx-auto py-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <motion.h1 
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 bg-gradient-to-r from-yellow-400 via-blue-400 to-amber-500 bg-clip-text text-transparent leading-tight drop-shadow-2xl"
            style={{
              textShadow: '0 0 30px rgba(251, 191, 36, 0.5), 0 0 60px rgba(59, 130, 246, 0.3)',
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.8))'
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            OCTAGON INFINITY
          </motion.h1>
          
          <motion.p
            className="text-xl sm:text-2xl md:text-3xl text-gray-100 mb-10 max-w-5xl mx-auto leading-relaxed px-4 drop-shadow-lg"
            style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            New & Exclusive Platform for Tokenizing Real Estate & Assets
            <br className="hidden sm:block" />
            <span className="text-amber-300 font-semibold block sm:inline">10,000 VIP Passes</span>
            <br className="hidden sm:block" />
            <span className="text-blue-300 font-medium block sm:inline">Generate Passive Income & Build Long-Term Wealth Portfolio</span>
          </motion.p>
        </motion.div>

        {/* Key Features */}
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <motion.div 
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-gray-700 hover:border-amber-500 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -10 }}
          >
            <SparklesIcon className="w-10 h-10 sm:w-12 sm:h-12 text-amber-400 mb-4 mx-auto" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">VIP Membership</h3>
            <p className="text-gray-400 text-sm sm:text-base">Exclusive access to premium investment projects across Europe</p>
          </motion.div>

          <motion.div 
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-gray-700 hover:border-blue-500 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -10 }}
          >
            <CurrencyDollarIcon className="w-10 h-10 sm:w-12 sm:h-12 text-blue-400 mb-4 mx-auto" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">12-25% Target Returns</h3>
            <p className="text-gray-400 text-sm sm:text-base">Projected annual returns from diversified real estate portfolio</p>
          </motion.div>

          <motion.div 
            className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-gray-700 hover:border-yellow-500 transition-all duration-300 sm:col-span-2 lg:col-span-1"
            whileHover={{ scale: 1.05, y: -10 }}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-400 mb-4 mx-auto flex items-center justify-center text-xl sm:text-2xl font-bold">
              €
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">€4,000 per NFT</h3>
            <p className="text-gray-400 text-sm sm:text-base">Each NFT represents €4,000 investment power in projects</p>
          </motion.div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.a
            href={mintingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto bg-gradient-to-r from-amber-600 to-blue-600 hover:from-amber-700 hover:to-blue-700 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-2xl text-lg sm:text-xl font-semibold shadow-lg hover:shadow-amber-500/25 transition-all duration-300 min-w-[200px] sm:min-w-[250px] inline-block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Mint Your NFT Now
          </motion.a>
          
          <motion.button
            onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
            className="w-full sm:w-auto border-2 border-amber-500 hover:bg-amber-500 text-amber-400 hover:text-white px-8 sm:px-10 py-3 sm:py-4 rounded-2xl text-lg sm:text-xl font-semibold transition-all duration-300 min-w-[200px] sm:min-w-[250px]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Calculate Returns
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;